import React, { useState, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';
import classNames from 'classnames';

import { selectDashboardOrgKey } from '@redux/dashboard.selectors';
import { extractOrgIdFromOrgKey } from '@legacy-modules/dashboard/utils/OrgKeyUtils';
import { OrgTreeContextType, useOrgTreeContext } from '@contexts/org-tree-context/org-tree.context';
import GenericTree from '@legacy-modules/metrics2/models/entities/organization/GenericTree';
import OrgUnitNode from '@legacy-modules/metrics2/models/entities/organization/OrgUnitNode';
import useRestbestand from './hooks/use-restbestand.hook';
import RestbestandSectionTable from './components/RestbestandSectionTable';
import ShipmentHistorySearch from '@legacy-modules/dashboard/containers/ShipmentHistorySearch';
import { collectSubtreeOrgIds } from './utils/OrgSubtreeUtils';
import { exportRestbestandToExcel } from './utils/RestbestandExportUtils';
import Styles from './RestbestandDataTable.module.scss';

type Props = {
  orgId?: number | null;
  datum?: string | null; // ISO yyyy-MM-dd
};

const RestbestandDataTable: React.FC<Props> = ({ orgId: propsOrgId, datum: propsDatum }) => {
  const reduxOrgKey = useSelector(selectDashboardOrgKey);

  // Extract numeric organization ID from Redux if not provided via props
  const extractedOrgId = useMemo(() => {
    if (!reduxOrgKey) return null;
    const raw = extractOrgIdFromOrgKey(reduxOrgKey);
    return raw ? parseInt(raw, 10) : null;
  }, [reduxOrgKey]);

  const effectiveOrgId = propsOrgId !== undefined ? propsOrgId : extractedOrgId;

  // Resolve organization display name from OrgTree if available
  let orgTreeContext: OrgTreeContextType = null;
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    orgTreeContext = useOrgTreeContext();
  } catch {
    // OrgTreeContext might not be mounted in isolated tests
  }

  const orgDisplayName = useMemo(() => {
    const rootNode = orgTreeContext?.orgTree?.rootNode;
    if (rootNode && reduxOrgKey) {
      const node = GenericTree.findNodeInTree(rootNode, (n: OrgUnitNode) => n.orgUnit?.orgKey === reduxOrgKey);
      if (node?.orgUnit?.name) {
        return `${node.orgUnit.name} (${effectiveOrgId ?? ''})`;
      }
    }
    return effectiveOrgId ? `ZSB (${effectiveOrgId})` : 'Bitte Organisation auswählen';
  }, [orgTreeContext, reduxOrgKey, effectiveOrgId]);

  // DMT-741 org-aggregation (HADV parity): if the selected org node has descendants (e.g. a Depot
  // with child ZSBs), resolve it to the full list of org IDs so their Restbestand is merged into one
  // combined result, exactly like HADV's multi-org selection behaviour. Falls back to the single
  // effectiveOrgId when the node isn't found (e.g. props-driven usage without an OrgTreeContext).
  const effectiveOrgIds = useMemo(() => {
    const rootNode = orgTreeContext?.orgTree?.rootNode;
    if (rootNode && reduxOrgKey && propsOrgId === undefined) {
      const node = GenericTree.findNodeInTree(rootNode, (n: OrgUnitNode) => n.orgUnit?.orgKey === reduxOrgKey);
      if (node) {
        const ids = collectSubtreeOrgIds(node);
        if (ids.length > 0) return ids;
      }
    }
    return effectiveOrgId != null ? [effectiveOrgId] : [];
  }, [orgTreeContext, reduxOrgKey, propsOrgId, effectiveOrgId]);

  // Operational default date: yesterday
  const defaultDate = useMemo(() => {
    return moment().subtract(1, 'days').format('YYYY-MM-DD');
  }, []);

  // Selecting a future date has no data and does not match HADV behaviour, so the
  // date input is capped at "today".
  const maxDate = useMemo(() => {
    return moment().format('YYYY-MM-DD');
  }, []);

  const [inputDate, setInputDate] = useState<string>(propsDatum ?? defaultDate);
  const [activeDate, setActiveDate] = useState<string>(propsDatum ?? defaultDate);

  const { data, isLoading, isError, refetch } = useRestbestand(effectiveOrgIds, activeDate);

  const [selectedBarcode, setSelectedBarcode] = useState<string | null>(null);
  const [popoverTarget, setPopoverTarget] = useState<HTMLElement | null>(null);

  const handleBarcodeClick = useCallback((barcode: string, target: HTMLElement) => {
    setSelectedBarcode(barcode);
    setPopoverTarget(target);
  }, []);

  const closePopover = useCallback(() => {
    setSelectedBarcode(null);
    setPopoverTarget(null);
  }, []);

  const handleAnzeigen = useCallback(() => {
    if (inputDate) {
      // Defensive clamp: ignore a future date even if it reached state via a non-native
      // input (e.g. typed/pasted), since the <input max> only blocks the native picker UI.
      const clamped = inputDate > maxDate ? maxDate : inputDate;
      setInputDate(clamped);
      setActiveDate(clamped);
    }
  }, [inputDate, maxDate]);

  const handleReset = useCallback(() => {
    setInputDate(defaultDate);
    setActiveDate(defaultDate);
  }, [defaultDate]);

  const handleExport = useCallback(() => {
    if (data) {
      exportRestbestandToExcel(data, effectiveOrgIds.join('-') || 'alle', activeDate);
    }
  }, [data, effectiveOrgIds, activeDate]);

  // Formatted last calculation timestamp (HADV parity)
  const calculationTimestamp = useMemo(() => {
    return moment().format('DD.MM.YYYY 12:00');
  }, []);

  return (
    <div className={Styles.Container}>
      {/* Selektionskriterien Section (AC2 & AC7) */}
      <div className={Styles.SelectionBox}>
        <div className={Styles.SelectionTitle}>Selektionskriterien</div>
        <div className={Styles.SelectionGrid}>
          <div className={Styles.FieldLabel}>Datum</div>
          <div className={Styles.FieldControl}>
            <input
              type='date'
              className={Styles.DateInput}
              value={inputDate}
              max={maxDate}
              onChange={(e) => setInputDate(e.target.value)}
              aria-label='Selektionsdatum'
            />
          </div>

          <div className={Styles.FieldLabel}>Datum / Uhrzeit der letzten Berechnung</div>
          <div className={Styles.FieldControl}>
            <input
              type='text'
              readOnly
              className={Styles.ReadOnlyField}
              value={calculationTimestamp}
              aria-label='Datum und Uhrzeit der letzten Berechnung'
            />
          </div>

          <div className={Styles.FieldLabel}>Organisation</div>
          <div className={Styles.FieldControl} style={{ gridColumn: 'span 3' }}>
            <div className={Styles.OrgDisplay}>{orgDisplayName}</div>
          </div>
        </div>

        <div className={Styles.SelectionActions}>
          <button type='button' className={Styles.AnzeigenButton} onClick={handleAnzeigen}>
            Anzeigen
          </button>
          <button type='button' className={Styles.ResetButton} onClick={handleReset}>
            Zurücksetzen
          </button>
        </div>
      </div>

      {/* Bericht Header with Navigation Tabs (AC1 & AC7) */}
      <div className={Styles.BerichtHeader}>
        <div className={Styles.BerichtTitle}>Bericht</div>
        <div className={Styles.TabBar}>
          <button type='button' className={classNames(Styles.TabButton, Styles.ActiveTab)}>
            Restbestand
          </button>
        </div>
        <div className={Styles.FilterRow}>
          <select className={Styles.SubfilterSelect} disabled aria-label='Filter Kategorie'>
            <option value='alle'>alle</option>
          </select>
        </div>
      </div>

      {/* States */}
      {effectiveOrgIds.length === 0 && (
        <div className={Styles.State}>
          Bitte wählen Sie eine Organisation (ZSB) im oberen Menü aus, um den Restbestand anzuzeigen.
        </div>
      )}

      {effectiveOrgIds.length > 0 && isLoading && <div className={Styles.State}>Lade Restbestand…</div>}

      {effectiveOrgIds.length > 0 && !isLoading && (isError || !data) && (
        <div className={Styles.State}>
          Restbestand konnte nicht geladen werden.{' '}
          <button type='button' className={Styles.ResetButton} onClick={() => refetch()}>
            Erneut versuchen
          </button>
        </div>
      )}

      {/* 2x2 Quadrant Grid (AC3, AC4, AC5, AC6, AC7) */}
      {effectiveOrgIds.length > 0 && !isLoading && data && (
        <>
          <div className={Styles.ExportRow}>
            <button type='button' className={Styles.ExportButton} onClick={handleExport}>
              Export (Excel)
            </button>
          </div>
          <div className={Styles.Grid}>
            <RestbestandSectionTable
              title='Restbestand dv. Liegengebliebene'
              rows={data.liegengebliebene}
              onBarcodeClick={handleBarcodeClick}
            />
            <RestbestandSectionTable
              title='Restbestand dv. Lagernde'
              rows={data.lagernde}
              onBarcodeClick={handleBarcodeClick}
            />
            <RestbestandSectionTable
              title='Restbestand dv. AFNUS'
              rows={data.afnus}
              onBarcodeClick={handleBarcodeClick}
            />
            <RestbestandSectionTable
              title='Bestandsbereinigung'
              rows={data.bereinigung}
              onBarcodeClick={handleBarcodeClick}
            />
          </div>
        </>
      )}

      {/* Shipment History Popover Modal */}
      {selectedBarcode && (
        <ShipmentHistorySearch
          barcode={selectedBarcode}
          visible={!!selectedBarcode}
          onClose={closePopover}
          target={popoverTarget}
        />
      )}
    </div>
  );
};

export default RestbestandDataTable;
